/**
 * src/config/app-entitlements.ts
 *
 * Enterprise App Entitlements & Token-Gating Registry.
 * Decouples business/enterprise modules from the consumer social portal:
 * - On herx.org (Public Portal): Enterprise apps are hidden by default, visible only in an 'Extensions Store' unlocked via credits or license tiers.
 * - On mesh.herx.org (Private Mesh): All enterprise apps are unlocked by default for air-gapped operations.
 */

export type AppId = 'crm' | 'pos' | 'taskflow' | 'payroll' | 'disaster-recovery' | 'invoicing';

export interface AppEntitlement {
  id: AppId;
  name: string;
  category: 'enterprise' | 'utility';
  description: string;
  iconName: string;
  requiredTier?: number;        // e.g. Tier 3 Sovereign Score
  tokenPricePerEpoch?: number; // Cost in earned PoPR/Karma credits (e.g. 50 credits/month)
  isAirgappedDefault: boolean; // Active only on mesh.herx.org by default
}

export const APP_CATALOG: Record<AppId, AppEntitlement> = {
  crm: {
    id: 'crm',
    name: 'Sovereign CRM',
    category: 'enterprise',
    description: 'Encrypted client pipelines, contact ledger, and deal tracking.',
    iconName: 'Users',
    tokenPricePerEpoch: 50,
    isAirgappedDefault: true,
  },
  pos: {
    id: 'pos',
    name: 'Point of Sale (POS)',
    category: 'enterprise',
    description: 'Hardware POS scanner, barcode generator, and cash register.',
    iconName: 'ShoppingBag',
    tokenPricePerEpoch: 100,
    isAirgappedDefault: true,
  },
  taskflow: {
    id: 'taskflow',
    name: 'TaskFlow Kanban',
    category: 'enterprise',
    description: 'Decentralized sprint planning, task boards, and scrum burndown.',
    iconName: 'Trello',
    tokenPricePerEpoch: 25,
    isAirgappedDefault: true,
  },
  payroll: {
    id: 'payroll',
    name: 'Statutory Payroll Engine',
    category: 'enterprise',
    description: 'EPF/KWSP, SOCSO, EIS, and PCB automated payroll computation.',
    iconName: 'DollarSign',
    tokenPricePerEpoch: 150,
    isAirgappedDefault: true,
  },
  invoicing: {
    id: 'invoicing',
    name: 'E-Invoicing & EFRIS Bridge',
    category: 'enterprise',
    description: 'Cryptographically sealed commercial invoices and tax audit compliance.',
    iconName: 'FileText',
    tokenPricePerEpoch: 75,
    isAirgappedDefault: true,
  },
  'disaster-recovery': {
    id: 'disaster-recovery',
    name: 'DR Failover Cluster',
    category: 'enterprise',
    description: 'Hot-standby node failover, PostgreSQL WAL replication, and raft health.',
    iconName: 'Server',
    requiredTier: 4,
    tokenPricePerEpoch: 200,
    isAirgappedDefault: true,
  },
};

/**
 * Checks whether a given app is unlocked for the active session.
 */
export function isAppUnlocked(
  appId: AppId,
  isPrivateMeshHost: boolean,
  unlockedAppIds: Set<string> = new Set()
): boolean {
  // On mesh.herx.org or private mesh mode, all enterprise apps are available
  if (isPrivateMeshHost) {
    return true;
  }
  // On public portal, requires explicit unlock token or entitlement
  return unlockedAppIds.has(appId);
}
